import ThumbImg from "../media/ThumbImg";
import React from 'react';
import StatusWidget from './StatusWidget';
import { readableNameColor } from '../../lib/nameColor';

type UserRowData = {
  id: string;
  name?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  nameColor?: string | null;
  status?: string | null; // activity status text
  rawStatus?: string | null; // user's presence mode: 'online' | 'mobile' | 'idle' | 'dnd' | 'invisible' | 'offline'
  online?: boolean;
  onMobile?: boolean;
  isIdle?: boolean; // computed from 10-min inactivity timer
  activityText?: string | null; // e.g., "Active 5m ago"
  roles?: { id: string; name: string; color?: string | null }[];
};

function UserRow({ data, onClick }: { data: UserRowData; onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void }) {
  const { name, username, avatarUrl, nameColor, status, rawStatus, online, onMobile, isIdle, activityText /* roles intentionally hidden */ } = data;
  const presence: 'online'|'mobile'|'idle'|'dnd'|'offline' = (rawStatus === 'invisible' || rawStatus === 'offline')
    ? 'offline'
    : (rawStatus === 'dnd')
      ? 'dnd'
      : (rawStatus === 'idle' || isIdle)
        ? 'idle'
        : (online ? (onMobile ? 'mobile' : 'online') : 'offline');
  const hideStatus = presence === 'offline';
  // Prefer the user's own local "What I'm up to" message if this row is for me
  const resolvedStatus = (() => {
    try {
      const raw = localStorage.getItem('user');
      const me = raw ? JSON.parse(raw) : null;
      if (me && me.id && data?.id && String(me.id) === String(data.id)) {
        const act = localStorage.getItem('profile.activity') || '';
        if (act && String(act).trim()) return String(act).trim();
      }
    } catch {}
    return status || null;
  })();
  const displayStatus = hideStatus ? null : resolvedStatus;
  const nameStyle = hideStatus ? undefined : (readableNameColor(nameColor) ? { color: readableNameColor(nameColor) } : undefined);
  const nameTextClass = hideStatus ? 'text-fg-3' : 'text-fg';
  return (
    <div className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-neutral-800/40 group relative">
      <button className="flex-1 text-left flex items-center gap-2" onClick={onClick} aria-label={`Open profile for ${name || username}`}>
        <div className="relative h-8 w-8">
          {(() => {
            // No presence ring for offline or invisible
            // Blue for mobile-online, Emerald for desktop-online
            const ring = (presence==='mobile') ? '#0ea5e9' : (presence==='online') ? '#10b981' : (presence==='idle') ? '#f59e0b' : (presence==='dnd') ? '#ef4444' : null;
            return ring ? (<span className="pointer-events-none absolute -inset-0.5 rounded-full" style={{ border: `2px solid ${ring}` }}></span>) : null;
          })()}
          <div className="h-8 w-8 rounded-full overflow-hidden bg-neutral-800 border border-neutral-700 flex items-center justify-center">
            {avatarUrl ? <ThumbImg w={56} src={avatarUrl} alt="avatar" className="h-full w-full object-cover"/> : <span className="text-meta text-fg-2">{(name?.[0]||username?.[0]||'?').toUpperCase()}</span>}
          </div>
        </div>
        <div className="min-w-0">
          <div className={`truncate text-sm ${nameTextClass}`} style={nameStyle}>
            <span className="font-medium">{name || username}</span>
            {/* presence dot removed; ring colour indicates presence */}
          </div>
          {/* Roles hidden in people column */}
          <StatusWidget statusText={displayStatus} presence={presence} activity={activityText || null} />
        </div>
      </button>
      {/* Roles tooltip hidden */}
    </div>
  );
}

export default UserRow;
